import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowLeft01Icon, ArrowRight01Icon, CheckmarkCircle02Icon, Loading02Icon } from '@hugeicons/core-free-icons';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router';
import { api, apiError } from '@/lib/api';
import { PageHeader } from '@/components/common/Bits';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { UserPicker } from '@/components/common/UserPicker';

const STEPS = ['Identificación', 'Tecnología', 'Datos', 'Uso e impacto', 'Valor y evidencia', 'Evaluación inicial'];

const Field = ({ label, children, required }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-muted-foreground">
      {label}
      {required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
    {children}
  </div>
);

export default function NewInitiative() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill || {};
  const [catalogs, setCatalogs] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: '',
    description: '',
    use_case: '',
    department: '',
    business_owner: '',
    tech_owner: '',
    clinical_owner: '',
    provider: '',
    model: '',
    model_version: '1.0',
    asset_type: 'Caso de uso',
    integrations: '',
    data_types: '',
    data_classification: 'Interna',
    personal_data: false,
    clinical_data: false,
    sensitive_data: false,
    retention: '12 meses',
    processing_region: 'Chile',
    estimated_users: 10,
    usage_volume: '',
    automated_decisions: false,
    human_validation: true,
    expected_value: 'Medio',
    roi_estimate: '',
    sponsor: '',
    value_axis: 'Personas',
    value_hypothesis: '',
    value_metric: '',
    value_baseline: '',
    value_target: '',
    value_unit: '',
    benefit_type: 'Capacidad liberada',
    annual_value_clp: '',
    investment_clp: '',
    tco_clp: '',
    attribution_method: '',
    benefit_owner: '',
    confidence: 'Baja',
    stop_criterion: '',
    inherent_risk: 'Medio',
    next_milestone: '',
    next_milestone_date: '',
    similar_capability_id: '',
    ...prefill,
  });
  const [capabilities, setCapabilities] = useState([]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get('/catalogs').then(({ data }) => setCatalogs(data));
    api
      .get('/capacidades')
      .then(({ data }) => setCapabilities(data || []))
      .catch(() => {});
  }, []);

  const validators = [() => null, () => null, () => null, () => null, () => null, () => null];

  const next = () => {
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const suggestedControls = [
    f.clinical_data && 'Validación clínica',
    f.personal_data && 'Revisión de Protección de Datos',
    f.automated_decisions && 'Human-in-the-loop',
    'Registro de interacciones',
    f.provider && f.provider !== 'Interno UC CHRISTUS' && 'Revisión contractual',
  ].filter(Boolean);

  const route =
    f.clinical_data || f.inherent_risk === 'Alto' || f.inherent_risk === 'Crítico' || f.inherent_risk === 'Medio-Alto'
      ? 'Comité de IA (con revisiones DPO / clínica según corresponda)'
      : 'Aprobación del área responsable';

  const submit = async () => {
    // Solo validar nombre mínimo para poder crear
    if (!f.name?.trim()) {
      toast.error('Ingrese al menos el nombre de la iniciativa');
      setStep(0);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: f.name,
        description: f.description,
        use_case: f.use_case,
        department: f.department || 'Por definir',
        business_owner: f.business_owner || null,
        tech_owner: f.tech_owner || null,
        clinical_owner: f.clinical_owner || null,
        provider: f.provider,
        model: f.model,
        model_version: f.model_version,
        asset_type: f.asset_type,
        integrations: f.integrations
          ? f.integrations
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        data_types: f.data_types
          ? f.data_types
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        data_classification: f.data_classification,
        personal_data: f.personal_data,
        clinical_data: f.clinical_data,
        sensitive_data: f.sensitive_data,
        retention: f.retention,
        processing_region: f.processing_region,
        estimated_users: Number(f.estimated_users) || 0,
        usage_volume: f.usage_volume,
        automated_decisions: f.automated_decisions,
        human_validation: f.human_validation,
        expected_value: f.expected_value,
        roi_estimate: f.roi_estimate,
        inherent_risk: f.inherent_risk,
        next_milestone: f.next_milestone || null,
        next_milestone_date: f.next_milestone_date ? new Date(f.next_milestone_date).toISOString() : null,
        similar_capability_id: f.similar_capability_id || null,
        sponsor: f.sponsor || null,
        value_axis: f.value_axis || null,
        value_hypothesis: f.value_hypothesis || null,
        value_metric: f.value_metric || null,
        value_unit: f.value_unit || null,
        value_baseline: f.value_baseline === '' ? null : Number(f.value_baseline),
        value_target: f.value_target === '' ? null : Number(f.value_target),
        benefit_type: f.benefit_type || null,
        benefit_owner: f.benefit_owner || null,
        annual_value_clp: f.annual_value_clp === '' ? null : Number(f.annual_value_clp),
        investment_clp: f.investment_clp === '' ? null : Number(f.investment_clp),
        tco_clp: f.tco_clp === '' ? null : Number(f.tco_clp),
        attribution_method: f.attribution_method || null,
        confidence: f.confidence || null,
        stop_criterion: f.stop_criterion || null,
      };
      const { data } = await api.post('/initiatives', payload);
      toast.success(`Iniciativa ${data.code} creada. Se generó la tarea de evaluación de riesgo.`);
      navigate(`/iniciativas/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!catalogs) return null;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <PageHeader
        title="Registrar nueva iniciativa"
        subtitle="Flujo guiado de registro. Al finalizar se crea la iniciativa, se registra el evento de auditoría y se generan las tareas de evaluación."
        testId="wizard-header"
      />

      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          {STEPS.map((s, i) => {
            const isValid = validators[i]() === null;
            return (
              <button
                key={s}
                onClick={() => setStep(i)}
                className={cn(
                  'text-[11px] sm:text-xs font-medium transition-colors cursor-pointer',
                  i === step ? 'text-primary' : isValid ? 'text-accent' : 'text-muted-foreground',
                )}>
                {isValid && i !== step && (
                  <HugeiconsIcon icon={CheckmarkCircle02Icon} className="h-3 w-3 inline mr-1" />
                )}
                {i + 1}. {s}
              </button>
            );
          })}
        </div>
        <Progress value={((step + 1) / STEPS.length) * 100} className="h-1.5" />
      </div>

      <Card className="shadow-card border-border">
        <CardContent className="p-6 sm:p-8 space-y-5" data-testid={`wizard-step-${step}`}>
          {step === 0 && (
            <>
              <Field label="Nombre de la iniciativa" required>
                <Input
                  value={f.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="Ej: Asistente de triage digital"
                  data-testid="wizard-name"
                />
              </Field>
              <Field label="Descripción">
                <Textarea
                  value={f.description}
                  onChange={(e) => set('description', e.target.value)}
                  rows={3}
                  placeholder="Qué hace la solución y en qué contexto se utiliza"
                  data-testid="wizard-description"
                />
              </Field>
              <Field label="Problema que resuelve / caso de uso">
                <Input
                  value={f.use_case}
                  onChange={(e) => set('use_case', e.target.value)}
                  placeholder="Ej: reducción de tiempos de espera"
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Departamento" required>
                  <Select value={f.department} onValueChange={(v) => set('department', v)}>
                    <SelectTrigger data-testid="wizard-department">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {catalogs.departments.map((d) => (
                        <SelectItem key={d} value={d}>
                          {d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Resp. de negocio" required>
                  <UserPicker
                    value={f.business_owner}
                    onSelect={(user) => set('business_owner', `${user.name} <${user.email}>`)}
                    onClear={() => set('business_owner', '')}
                    placeholder="Buscar responsable de negocio..."
                    testId="wizard-owner"
                  />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Próximo hito">
                  <Input
                    value={f.next_milestone}
                    onChange={(e) => set('next_milestone', e.target.value)}
                    placeholder="Ej: Entrega de POC"
                    data-testid="wizard-next-milestone"
                  />
                </Field>
                <Field label="Fecha del hito">
                  <Input
                    type="date"
                    value={f.next_milestone_date}
                    onChange={(e) => set('next_milestone_date', e.target.value)}
                    data-testid="wizard-milestone-date"
                  />
                </Field>
              </div>
              <Field label="¿Existe una solución similar?">
                <Select
                  value={f.similar_capability_id || '__none__'}
                  onValueChange={(v) => set('similar_capability_id', v === '__none__' ? '' : v)}>
                  <SelectTrigger data-testid="wizard-similar-capability">
                    <SelectValue placeholder="Ninguna — buscar en capacidades existentes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Ninguna</SelectItem>
                    {capabilities.map((cap) => (
                      <SelectItem key={cap.id} value={cap.id}>
                        {cap.name} ({cap.category})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {f.similar_capability_id && (
                  <p className="text-xs text-muted-foreground mt-1">
                    <Link to="/capacidades" className="text-primary hover:underline">
                      Ver mapa de capacidades →
                    </Link>
                  </p>
                )}
              </Field>
            </>
          )}

          {step === 1 && (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Proveedor">
                  <Select
                    value={f.provider || '__none__'}
                    onValueChange={(v) => set('provider', v === '__none__' ? '' : v)}>
                    <SelectTrigger data-testid="wizard-provider">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Ninguno</SelectItem>
                      {catalogs.providers.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Tipo de activo">
                  <Select value={f.asset_type} onValueChange={(v) => set('asset_type', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {catalogs.asset_types.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Modelo o servicio">
                  <Input
                    value={f.model}
                    onChange={(e) => set('model', e.target.value)}
                    placeholder="Ej: GPT-4o, modelo interno"
                    data-testid="wizard-model"
                  />
                </Field>
                <Field label="Versión">
                  <Input value={f.model_version} onChange={(e) => set('model_version', e.target.value)} />
                </Field>
              </div>
              <Field label="Integraciones y dependencias (separadas por coma)">
                <Input
                  value={f.integrations}
                  onChange={(e) => set('integrations', e.target.value)}
                  placeholder="HIS, ERP SAP, Teams"
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Responsable técnico">
                  <UserPicker
                    value={f.tech_owner}
                    onSelect={(user) => set('tech_owner', `${user.name} <${user.email}>`)}
                    onClear={() => set('tech_owner', '')}
                    placeholder="Buscar responsable técnico..."
                  />
                </Field>
                <Field label="Responsable clínico (si corresponde)">
                  <UserPicker
                    value={f.clinical_owner}
                    onSelect={(user) => set('clinical_owner', `${user.name} <${user.email}>`)}
                    onClear={() => set('clinical_owner', '')}
                    placeholder="Buscar responsable clínico..."
                  />
                </Field>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <Field label="Tipos de datos procesados (separados por coma)">
                <Input
                  value={f.data_types}
                  onChange={(e) => set('data_types', e.target.value)}
                  placeholder="Ficha clínica, datos demográficos"
                  data-testid="wizard-datatypes"
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Clasificación de datos" required>
                  <Select value={f.data_classification} onValueChange={(v) => set('data_classification', v)}>
                    <SelectTrigger data-testid="wizard-classification">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {catalogs.data_classifications.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Ubicación de procesamiento">
                  <Input value={f.processing_region} onChange={(e) => set('processing_region', e.target.value)} />
                </Field>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  ['personal_data', 'Datos personales'],
                  ['clinical_data', 'Datos clínicos'],
                  ['sensitive_data', 'Datos sensibles'],
                ].map(([k, l]) => (
                  <div key={k} className="flex items-center justify-between rounded-lg border border-border p-3">
                    <span className="text-sm">{l}</span>
                    <Switch checked={f[k]} onCheckedChange={(v) => set(k, v)} data-testid={`wizard-${k}`} />
                  </div>
                ))}
              </div>
              <Field label="Retención de datos">
                <Input value={f.retention} onChange={(e) => set('retention', e.target.value)} />
              </Field>
            </>
          )}

          {step === 3 && (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Usuarios estimados">
                  <Input
                    type="number"
                    value={f.estimated_users}
                    onChange={(e) => set('estimated_users', e.target.value)}
                    data-testid="wizard-users"
                  />
                </Field>
                <Field label="Volumen de uso estimado">
                  <Input
                    value={f.usage_volume}
                    onChange={(e) => set('usage_volume', e.target.value)}
                    placeholder="~2.000 consultas/mes"
                  />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <div>
                    <p className="text-sm">Decisiones automatizadas</p>
                    <p className="text-[11px] text-muted-foreground">Sin intervención humana previa</p>
                  </div>
                  <Switch checked={f.automated_decisions} onCheckedChange={(v) => set('automated_decisions', v)} />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <div>
                    <p className="text-sm">Validación humana</p>
                    <p className="text-[11px] text-muted-foreground">Human-in-the-loop</p>
                  </div>
                  <Switch checked={f.human_validation} onCheckedChange={(v) => set('human_validation', v)} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Valor esperado">
                  <Select value={f.expected_value} onValueChange={(v) => set('expected_value', v)}>
                    <SelectTrigger data-testid="wizard-value">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['Muy alto', 'Alto', 'Medio', 'Bajo'].map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="ROI o impacto estimado">
                  <Input
                    value={f.roi_estimate}
                    onChange={(e) => set('roi_estimate', e.target.value)}
                    placeholder="Ej: -20% tiempo de espera"
                  />
                </Field>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
                <p className="text-sm font-semibold">El “para qué” es obligatorio</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  Esta declaración crea el libro de beneficios. No se considerará valor realizado hasta que exista
                  evidencia y validación independiente.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Eje de valor VIA" required>
                  <Select value={f.value_axis} onValueChange={(v) => set('value_axis', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['Personas', 'Operación', 'Productividad', 'Confianza'].map((axis) => (
                        <SelectItem key={axis} value={axis}>
                          {axis}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Patrocinador ejecutivo">
                  <UserPicker
                    value={f.sponsor}
                    onSelect={(user) => set('sponsor', `${user.name} <${user.email}>`)}
                    onClear={() => set('sponsor', '')}
                    placeholder="Buscar o escribir nombre del patrocinador..."
                    allowFreeText
                    onFreeText={(text) => set('sponsor', text)}
                  />
                </Field>
              </div>
              <Field label="Hipótesis de valor" required>
                <Textarea
                  value={f.value_hypothesis}
                  onChange={(e) => set('value_hypothesis', e.target.value)}
                  rows={3}
                  placeholder="Si hacemos X para Y, esperamos Z porque..."
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Métrica principal" required>
                  <Input
                    value={f.value_metric}
                    onChange={(e) => set('value_metric', e.target.value)}
                    placeholder="Ej.: horas liberadas por semana"
                  />
                </Field>
                <Field label="Unidad">
                  <Input
                    value={f.value_unit}
                    onChange={(e) => set('value_unit', e.target.value)}
                    placeholder="horas, %, CLP"
                  />
                </Field>
                <Field label="Línea base">
                  <Input
                    type="number"
                    value={f.value_baseline}
                    onChange={(e) => set('value_baseline', e.target.value)}
                  />
                </Field>
                <Field label="Meta" required>
                  <Input type="number" value={f.value_target} onChange={(e) => set('value_target', e.target.value)} />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Tipo de beneficio">
                  <Select value={f.benefit_type} onValueChange={(v) => set('benefit_type', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['Capacidad liberada', 'Ahorro evitable', 'Ingreso incremental', 'Calidad y seguridad'].map(
                        (type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Responsable del beneficio" required>
                  <UserPicker
                    value={f.benefit_owner}
                    onSelect={(user) => set('benefit_owner', `${user.name} <${user.email}>`)}
                    onClear={() => set('benefit_owner', '')}
                    placeholder="Buscar o escribir responsable..."
                    allowFreeText
                    onFreeText={(text) => set('benefit_owner', text)}
                  />
                </Field>
                <Field label="Valor anual estimado (CLP)">
                  <Input
                    type="number"
                    value={f.annual_value_clp}
                    onChange={(e) => set('annual_value_clp', e.target.value)}
                  />
                </Field>
                <Field label="Inversión estimada (CLP)">
                  <Input
                    type="number"
                    value={f.investment_clp}
                    onChange={(e) => set('investment_clp', e.target.value)}
                  />
                </Field>
                <Field label="TCO anual estimado (CLP)">
                  <Input type="number" value={f.tco_clp} onChange={(e) => set('tco_clp', e.target.value)} />
                </Field>
                <Field label="Confianza inicial">
                  <Select value={f.confidence} onValueChange={(v) => set('confidence', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['Baja', 'Media', 'Alta'].map((confidence) => (
                        <SelectItem key={confidence} value={confidence}>
                          {confidence}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Método de atribución">
                <Textarea
                  value={f.attribution_method}
                  onChange={(e) => set('attribution_method', e.target.value)}
                  rows={2}
                  placeholder="Cómo separaremos el efecto de la IA de otros cambios"
                />
              </Field>
              <Field label="Criterio para detener o rediseñar" required>
                <Textarea
                  value={f.stop_criterion}
                  onChange={(e) => set('stop_criterion', e.target.value)}
                  rows={2}
                  placeholder="Ej.: detener si la tasa de error supera 3% o no hay mejora tras 90 días"
                />
              </Field>
            </>
          )}

          {step === 5 && (
            <>
              <Field label="Riesgo preliminar">
                <Select value={f.inherent_risk} onValueChange={(v) => set('inherent_risk', v)}>
                  <SelectTrigger data-testid="wizard-risk">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {catalogs.risk_levels.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="rounded-xl border border-border bg-gradient-panel p-5 space-y-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Controles sugeridos
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {suggestedControls.map((c) => (
                      <span
                        key={c}
                        className="px-2.5 py-1 rounded-md bg-accent/10 text-accent border border-accent/25 text-xs font-medium">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Ruta de aprobación
                  </p>
                  <p className="text-sm mt-1.5">{route}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Evidencias necesarias
                  </p>
                  <p className="text-sm mt-1.5 text-muted-foreground">
                    {f.clinical_data
                      ? 'DPIA, validación clínica, documentación del proveedor, plan de monitoreo'
                      : f.personal_data
                        ? 'DPIA, documentación del proveedor, plan de monitoreo'
                        : 'Documentación del proveedor y caso de negocio'}
                  </p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between mt-6">
        <Button
          variant="outline"
          onClick={() => (step === 0 ? navigate(-1) : setStep(step - 1))}
          className="border-border"
          data-testid="wizard-back-btn">
          <HugeiconsIcon icon={ArrowLeft01Icon} className="h-4 w-4 mr-1" /> {step === 0 ? 'Cancelar' : 'Anterior'}
        </Button>
        {step < STEPS.length - 1 ? (
          <Button
            onClick={next}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
            data-testid="wizard-next-btn">
            Siguiente <HugeiconsIcon icon={ArrowRight01Icon} className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-accent hover:bg-accent/90 text-accent-foreground"
            data-testid="wizard-submit-btn">
            {saving ? <HugeiconsIcon icon={Loading02Icon} className="h-4 w-4 animate-spin" /> : 'Crear iniciativa'}
          </Button>
        )}
      </div>
    </div>
  );
}
